import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Loader2, 
  Calendar as CalendarIcon, 
  Clock, 
  MapPin, 
  ChevronLeft,
  ChevronRight,
  X,
  Repeat,
  Building2,
} from "lucide-react";
import { getPublicEvents } from "../services/api";
const API_URL = import.meta.env.VITE_API_URL;

const HOUR_HEIGHT = 50;
const HOURS = Array.from({ length: 24 }, (_, i) => i);

// Convert Firestore timestamps to JS Date
const toJsDate = (val) => {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (val.toDate && typeof val.toDate === "function") return val.toDate();
  if (val.seconds) return new Date(val.seconds * 1000);
  if (typeof val === "string") return new Date(val);
  return new Date();
};

// Format date key (fallback when dateKey not available from backend)
const formatDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Parse time to minutes
const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const parts = timeStr.trim().split(/\s+/);
  if (parts.length < 2) return 0;
  const [hours, minutes] = parts[0].split(':').map(Number);
  const ampm = parts[1].toLowerCase();
  let totalHours = hours;
  if (ampm === 'pm' && hours < 12) totalHours += 12;
  if (ampm === 'am' && hours === 12) totalHours = 0;
  return totalHours * 60 + (minutes || 0);
};

// Format recurrence rule
const formatRecurrenceRule = (recurrence, event = null) => {
  if (!recurrence || recurrence.type === "never") return null;
  
  const { type, frequency, interval, byDay, bySetPos, byMonthDay } = recurrence;
  
  const dayMap = {
    SU: 'Sunday', MO: 'Monday', TU: 'Tuesday', WE: 'Wednesday',
    TH: 'Thursday', FR: 'Friday', SA: 'Saturday'
  };
  
  const ordinalMap = {
    1: 'First', 2: 'Second', 3: 'Third', 4: 'Fourth', 5: 'Fifth',
    '-1': 'Last', '-2': 'Second to last'
  };
  
  let baseText = "";
  
  if (type === "weekly" || frequency === "WEEKLY") {
    const days = byDay && byDay.length > 0 ? byDay.map(d => dayMap[d] || d).join(", ") : "";
    baseText = interval === 1 ? (days ? `Weekly on ${days}` : "Weekly") : `Every ${interval} weeks`;
  } else if (type === "daily" || frequency === "DAILY") {
    baseText = interval === 1 ? "Daily" : `Every ${interval} days`;
  } else if (type === "monthly" || frequency === "MONTHLY") {
    baseText = interval === 1 ? "Monthly" : `Every ${interval} months`;
  } else {
    baseText = "Recurring";
  }
  
  if (event?.timeType === 'prayer' && event?.prayerTimeConfig?.startPrayer) {
    baseText += ` after ${event.prayerTimeConfig.startPrayer}`;
  }
  
  return baseText;
};

// Get days that have events
const getDaysWithEvents = (events) => {
  const uniqueDates = new Set();
  
  events.forEach(event => {
    // Use dateKey from backend if available (timezone-correct)
    // Fall back to recurrenceInstanceId date, then event.date
    let dateKey;
    if (event.dateKey) {
      // Backend provides timezone-correct date key
      dateKey = event.dateKey;
    } else if (event.recurrenceInstanceId) {
      // recurrenceInstanceId format is "eventId_YYYY-MM-DD"
      const parts = event.recurrenceInstanceId.split('_');
      dateKey = parts.length > 1 ? parts[parts.length - 1] : null;
    } else {
      dateKey = formatDateKey(toJsDate(event.date));
    }
    if (dateKey) uniqueDates.add(dateKey);
  });
  
  return Array.from(uniqueDates)
    .map(dateStr => {
      const [y, m, d] = dateStr.split('-').map(Number);
      return new Date(y, m - 1, d);
    })
    .sort((a, b) => a - b);
};

// Group overlapping events for layout
const groupOverlappingEvents = (eventsArray) => {
  if (eventsArray.length === 0) return [];
  
  const sorted = [...eventsArray].sort((a, b) => {
    const aStart = parseTimeToMinutes(a.time);
    const bStart = parseTimeToMinutes(b.time);
    if (aStart !== bStart) return aStart - bStart;
    const aEnd = a.endTime ? parseTimeToMinutes(a.endTime) : aStart + 60;
    const bEnd = b.endTime ? parseTimeToMinutes(b.endTime) : bStart + 60;
    return aEnd - bEnd;
  });
  
  const columns = [];
  let lastEventEnding = null;
  const groups = [];
  
  sorted.forEach((event) => {
    const eventStart = parseTimeToMinutes(event.time);
    const eventEnd = event.endTime ? parseTimeToMinutes(event.endTime) : eventStart + 60;
    
    if (lastEventEnding !== null && eventStart >= lastEventEnding) {
      groups.push(columns.slice());
      columns.length = 0;
      lastEventEnding = null;
    }
    
    let placed = false;
    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      const lastInCol = col[col.length - 1];
      const lastEnd = lastInCol.endTime 
        ? parseTimeToMinutes(lastInCol.endTime)
        : parseTimeToMinutes(lastInCol.time) + 60;
      
      if (eventStart >= lastEnd) {
        col.push(event);
        placed = true;
        break;
      }
    }
    
    if (!placed) {
      columns.push([event]);
    }
    
    if (lastEventEnding === null || eventEnd > lastEventEnding) {
      lastEventEnding = eventEnd;
    }
  });
  
  if (columns.length > 0) {
    groups.push(columns);
  }
  
  const eventLayouts = [];
  
  groups.forEach(cols => {
    const numColumns = cols.length;
    cols.forEach((col, colIndex) => {
      col.forEach(event => {
        eventLayouts.push({
          event,
          column: colIndex,
          totalColumns: numColumns,
          colSpan: 1,
        });
      });
    });
  });
  
  return eventLayouts;
};

export default function EventDayWidget() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const theme = searchParams.get("theme") || "light";
  const color = searchParams.get("color") || "bf9a2f";
  const widthParam = searchParams.get("width") || "max";
  const heightParam = searchParams.get("height") || "max";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [masjidData, setMasjidData] = useState(null);
  const [masjidId, setMasjidId] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  const scrollRef = useRef(null);

  const isDark = theme === "dark";
  const accentColor = `#${color}`;
  const bgColor = isDark ? "#1a1a1a" : "#ffffff";
  const textColor = isDark ? "#ffffff" : "#0f172a";
  const mutedColor = isDark ? "#9ca3af" : "#64748b";
  const borderColor = isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)";
  const cardBg = isDark ? "#222" : "#fff";
  const sectionBg = isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)";
  const outerBorder = isDark ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.1)";

  // Container style with border and rounded corners
  const containerStyle = {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: bgColor,
    width: widthParam === "max" ? "100vw" : `${widthParam}px`,
    height: heightParam === "max" ? "100vh" : `${heightParam}px`,
    minHeight: "400px",
    borderRadius: widthParam === "max" && heightParam === "max" ? "0" : "12px",
    border: widthParam === "max" && heightParam === "max" ? "none" : outerBorder,
    overflow: "hidden",
    boxSizing: "border-box",
  };

  const allDays = getDaysWithEvents(events);
  const selectedDate = allDays[selectedDayIndex] || null;
  const dateKey = selectedDate ? formatDateKey(selectedDate) : null;
  
  const dayEvents = dateKey
    ? events.filter(event => {
        // Use dateKey from backend if available (timezone-correct)
        if (event.dateKey) {
          return event.dateKey === dateKey;
        }
        if (event.recurrenceInstanceId) {
          // recurrenceInstanceId format is "eventId_YYYY-MM-DD"
          const parts = event.recurrenceInstanceId.split('_');
          const eventDateKey = parts.length > 1 ? parts[parts.length - 1] : null;
          return eventDateKey === dateKey;
        }
        return formatDateKey(toJsDate(event.date)) === dateKey;
      })
    : [];

  // Load masjid info
  useEffect(() => {
    if (!token) return;

    const loadMasjidInfo = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/v1/widget-data/${token}`);
        if (!response.ok) throw new Error("Failed to load widget data");

        const data = await response.json();
        if (!data.ok) throw new Error(data.error || "Failed to load widget data");

        setMasjidData(data.masjid);
        setMasjidId(data.masjid.id || data.masjid.masjidId || data.masjid.key);
      } catch (err) {
        setError(err.message || "Failed to load masjid data");
      } finally {
        setLoading(false);
      }
    };

    loadMasjidInfo();
  }, [token]);

  // Fetch events via API
  useEffect(() => {
    if (!masjidId) return;

    const fetchEvents = async () => {
      try {
        setLoading(true);
        
        // Use the events API with masjid filter
        const result = await getPublicEvents({ 
          masjidId, 
          days: 90,  // 3 months
          limit: 200 
        });
        
        const eventsList = result.events || [];
        eventsList.sort((a, b) => toJsDate(a.date) - toJsDate(b.date));
        setEvents(eventsList);
      } catch (err) {
        console.error("Error fetching events:", err);
        setError(err.message || "Failed to load events");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [masjidId]);

  // Scroll to earliest event
  useEffect(() => {
    if (scrollRef.current && dayEvents.length > 0) {
      setTimeout(() => {
        const earliestTime = Math.min(...dayEvents.map(e => parseTimeToMinutes(e.time)));
        const earliestHour = Math.floor(earliestTime / 60);
        const scrollToHour = Math.max(0, earliestHour - 1);
        scrollRef.current?.scrollTo({ top: scrollToHour * HOUR_HEIGHT, behavior: "smooth" });
      }, 150);
    }
  }, [selectedDayIndex, dayEvents.length]);

  const goToPreviousDay = () => {
    if (selectedDayIndex > 0) setSelectedDayIndex(selectedDayIndex - 1);
  };

  const goToNextDay = () => {
    if (selectedDayIndex < allDays.length - 1) setSelectedDayIndex(selectedDayIndex + 1);
  };

  const isToday = (date) => formatDateKey(date) === formatDateKey(new Date());

  const eventLayouts = groupOverlappingEvents(dayEvents);

  // Event Detail Modal
  const EventDetailModal = ({ event, onClose }) => {
    if (!event) return null;

    const eventDate = toJsDate(event.date);
    const formattedDate = eventDate.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    return (
      <div 
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px",
          zIndex: 1000,
        }}
        onClick={onClose}
      >
        <div 
          style={{
            backgroundColor: cardBg,
            borderRadius: "16px",
            width: "100%",
            maxWidth: "400px",
            maxHeight: "80vh",
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${borderColor}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: textColor, lineHeight: "1.3" }}>
                {event.title}
              </h3>
              <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", color: mutedColor, flexShrink: 0 }}>
                <X size={20} />
              </button>
            </div>
            {event.masjid && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "8px" }}>
                <Building2 size={14} color={accentColor} />
                <span style={{ fontSize: "13px", fontWeight: "600", color: accentColor }}>{event.masjid}</span>
              </div>
            )}
          </div>

          <div style={{ padding: "16px 20px", overflowY: "auto", maxHeight: "calc(80vh - 120px)" }}>
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "11px", fontWeight: "700", color: mutedColor, letterSpacing: "0.5px", marginBottom: "8px" }}>WHEN</div>
              <div style={{ backgroundColor: sectionBg, borderRadius: "10px", padding: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <CalendarIcon size={18} color={accentColor} />
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: "600", color: textColor }}>{formattedDate}</div>
                    {event.time && (
                      <div style={{ fontSize: "13px", color: mutedColor, marginTop: "2px" }}>
                        {event.time}{event.endTime && ` - ${event.endTime}`}
                      </div>
                    )}
                  </div>
                </div>
                {event.recurrence && event.recurrence.type !== "never" && (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "10px", paddingTop: "10px", borderTop: `1px solid ${borderColor}` }}>
                    <Repeat size={18} color={accentColor} />
                    <span style={{ fontSize: "13px", color: textColor }}>{formatRecurrenceRule(event.recurrence, event)}</span>
                  </div>
                )}
              </div>
            </div>

            {(event.location || event.sublocation) && (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "11px", fontWeight: "700", color: mutedColor, letterSpacing: "0.5px", marginBottom: "8px" }}>WHERE</div>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none", display: "block" }}
                >
                  <div style={{ backgroundColor: sectionBg, borderRadius: "10px", padding: "12px", cursor: "pointer", transition: "all 0.2s" }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = sectionBg}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                      <MapPin size={18} color={accentColor} style={{ flexShrink: 0, marginTop: "2px" }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "14px", fontWeight: "600", color: accentColor }}>{event.location}</div>
                        {event.sublocation && <div style={{ fontSize: "13px", color: mutedColor, marginTop: "2px" }}>{event.sublocation}</div>}
                      </div>
                      <ChevronRight size={16} color={mutedColor} style={{ flexShrink: 0, marginTop: "2px" }} />
                    </div>
                  </div>
                </a>
              </div>
            )}

            {event.description && (
              <div>
                <div style={{ fontSize: "11px", fontWeight: "700", color: mutedColor, letterSpacing: "0.5px", marginBottom: "8px" }}>DESCRIPTION</div>
                <p style={{ margin: 0, fontSize: "14px", lineHeight: "1.6", color: isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)", whiteSpace: "pre-wrap" }}>
                  {event.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!token) {
    return (
      <div style={{ ...containerStyle, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🕌</div>
          <p style={{ color: "#ef4444", fontSize: "18px", fontWeight: "600", margin: 0 }}>Missing Widget Token</p>
          <p style={{ color: mutedColor, fontSize: "14px", marginTop: "8px" }}>Please configure this widget from your masjid dashboard</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ ...containerStyle, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <Loader2 style={{ width: "48px", height: "48px", color: accentColor, animation: "spin 1s linear infinite" }} />
        <p style={{ marginTop: "16px", color: mutedColor, fontSize: "14px" }}>Loading events...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...containerStyle, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px", textAlign: "center" }}>
        <div style={{ width: "56px", height: "56px", borderRadius: "50%", backgroundColor: "rgba(239, 68, 68, 0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
          <span style={{ fontSize: "28px" }}>⚠️</span>
        </div>
        <p style={{ color: "#ef4444", fontSize: "16px", fontWeight: "600", margin: 0 }}>{error}</p>
      </div>
    );
  }

  if (events.length === 0 || !selectedDate) {
    return (
      <div style={{ ...containerStyle, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 20px", textAlign: "center" }}>
        <CalendarIcon style={{ width: "64px", height: "64px", color: mutedColor, opacity: 0.3, marginBottom: "16px" }} />
        <h3 style={{ fontSize: "20px", fontWeight: "700", color: textColor, margin: "0 0 8px 0" }}>No Upcoming Events</h3>
        <p style={{ color: mutedColor, fontSize: "14px", margin: 0 }}>Check back later for new events from {masjidData?.name}</p>
      </div>
    );
  }

  return (
    <div style={{ ...containerStyle, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ textAlign: "center", padding: "12px 16px", borderBottom: `1px solid ${borderColor}` }}>
        <h2 style={{ margin: "0 0 4px 0", fontSize: "18px", fontWeight: "700", color: accentColor, letterSpacing: "0.5px" }}>
          EVENTS
        </h2>
        {masjidData?.name && (
          <p style={{ margin: "0", fontSize: "12px", color: mutedColor, fontWeight: "500" }}>{masjidData.name}</p>
        )}
      </div>

      {/* Day Tabs */}
      <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${borderColor}`, padding: "4px 8px" }}>
        <button 
          onClick={goToPreviousDay}
          disabled={selectedDayIndex === 0}
          style={{ background: "none", border: "none", padding: "4px", cursor: selectedDayIndex > 0 ? "pointer" : "default", color: selectedDayIndex > 0 ? textColor : `${textColor}33` }}
        >
          <ChevronLeft size={18} />
        </button>
        
        <div style={{ flex: 1, display: "flex", gap: "4px", overflowX: "auto", padding: "2px 4px", scrollbarWidth: "none" }}>
          {allDays.map((date, index) => {
            const isTodayDate = isToday(date);
            const isSelected = index === selectedDayIndex;
            
            return (
              <button
                key={index}
                onClick={() => setSelectedDayIndex(index)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "4px 10px",
                  borderRadius: "8px",
                  border: "none",
                  cursor: "pointer",
                  minWidth: "44px",
                  backgroundColor: isSelected ? accentColor : "transparent",
                  transition: "all 0.2s",
                }}
              >
                <span style={{ fontSize: "9px", fontWeight: "600", color: isSelected ? "#fff" : `${textColor}99`, textTransform: "uppercase" }}>
                  {date.toLocaleDateString('en-US', { weekday: 'short' })}
                </span>
                <span style={{ fontSize: "14px", fontWeight: "700", color: isSelected ? "#fff" : (isTodayDate ? accentColor : textColor), marginTop: "1px" }}>
                  {date.getDate()}
                </span>
              </button>
            );
          })}
        </div>
        
        <button 
          onClick={goToNextDay}
          disabled={selectedDayIndex >= allDays.length - 1}
          style={{ background: "none", border: "none", padding: "4px", cursor: selectedDayIndex < allDays.length - 1 ? "pointer" : "default", color: selectedDayIndex < allDays.length - 1 ? textColor : `${textColor}33` }}
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Day Grid */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ display: "flex", position: "relative" }}>
          {/* Time Labels */}
          <div style={{ width: "50px", flexShrink: 0 }}>
            {HOURS.map(hour => (
              <div key={hour} style={{ height: `${HOUR_HEIGHT}px`, display: "flex", justifyContent: "flex-end", paddingRight: "8px", paddingTop: "4px" }}>
                <span style={{ fontSize: "11px", fontWeight: "500", color: `${textColor}99` }}>
                  {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                </span>
              </div>
            ))}
          </div>
          
          {/* Events Column */}
          <div style={{ flex: 1, position: "relative" }}>
            {/* Hour lines */}
            {HOURS.map(hour => (
              <div key={hour} style={{ height: `${HOUR_HEIGHT}px`, borderBottom: `1px solid ${borderColor}` }} />
            ))}
            
            {/* Events */}
            {eventLayouts.map((layout, index) => {
              const { event, column, totalColumns } = layout;
              const eventMinutes = parseTimeToMinutes(event.time);
              const topPosition = (eventMinutes / 60) * HOUR_HEIGHT;
              const endMinutes = event.endTime ? parseTimeToMinutes(event.endTime) : eventMinutes + 60;
              const durationMinutes = endMinutes - eventMinutes;
              const eventHeight = Math.max((durationMinutes / 60) * HOUR_HEIGHT, 45);
              
              const leftPercent = (column / totalColumns) * 100;
              const widthPercent = (1 / totalColumns) * 100;
              
              return (
                <div
                  key={event.recurrenceInstanceId || event.id}
                  onClick={() => setSelectedEvent(event)}
                  style={{
                    position: "absolute",
                    top: `${topPosition}px`,
                    left: `calc(${leftPercent}% + 2px)`,
                    width: `calc(${widthPercent}% - 4px)`,
                    height: `${eventHeight}px`,
                    backgroundColor: cardBg,
                    borderTop: `3px solid ${accentColor}`,
                    borderRadius: "6px",
                    padding: "6px 8px",
                    overflow: "hidden",
                    cursor: "pointer",
                    boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.1)",
                    border: isDark ? "none" : `1px solid ${borderColor}`,
                    transition: "transform 0.2s, box-shadow 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.02)";
                    e.currentTarget.style.boxShadow = isDark ? "0 4px 12px rgba(0,0,0,0.4)" : "0 4px 12px rgba(0,0,0,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.1)";
                  }}
                >
                  <div style={{ fontSize: "10px", fontWeight: "600", color: `${textColor}99`, marginBottom: "2px" }}>
                    {event.time}{event.endTime && ` - ${event.endTime}`}
                  </div>
                  <div style={{ fontSize: "12px", fontWeight: "600", color: textColor, lineHeight: "1.3", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: eventHeight > 60 ? 2 : 1, WebkitBoxOrient: "vertical" }}>
                    {event.title}
                  </div>
                  {eventHeight > 70 && event.masjid && (
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "4px" }}>
                      <Building2 size={10} color={accentColor} />
                      <span style={{ fontSize: "10px", color: accentColor }}>{event.masjid}</span>
                    </div>
                  )}
                </div>
              );
            })}

            {/* No events message */}
            {dayEvents.length === 0 && (
              <div style={{ position: "absolute", top: "40%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                <CalendarIcon size={48} color={`${textColor}33`} />
                <p style={{ color: `${textColor}66`, fontSize: "15px", marginTop: "12px", fontWeight: "500" }}>No events for this day</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: "10px", borderTop: `1px solid ${borderColor}`, textAlign: "center" }}>
        <a href="https://muslimplus.org" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "11px", color: mutedColor, textDecoration: "none" }}>
          <span>Powered by</span>
          <span style={{ fontWeight: "700", color: accentColor }}>Muslim+</span>
        </a>
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}
    </div>
  );
}
